import React, { useRef, useState } from "react";
import "./TextBox.scss";

// password import
import { ArrowLeft, ArrowRight, Eye } from "../../icons";

// datepicker import
import ReactDOM from "react-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const Textbox = ({
  type = "text", // type of the input filed
  name = "", // name of input
  value, // value for input
  size = "md", // size of input
  widthSize, // input width
  icon: Icon = null, // to add icon to input filed
  iconSide = "left", // if icon is set then in which side it place
  disabled = false, // for disable input
  className = "",
  placeholder = "",
  ...props
}) => {
  /* useStates */

  // passwords
  const [showPassword, setShowPassword] = useState(false);

  // date picker
  const [startDate, setStartDate] = useState(new Date());
  const [isYearPickerOpen, setYearPickerOpen] = useState(false);
  const [isMonthPickerOpen, setMonthPickerOpen] = useState(false);

  /* end useStates */

  /* useRef */
  const parentRef = useRef(null); // for createPortal
  /* end useRef */

  /* start password logic */

  const isPasswordType = type === "password"; // check the type is password or not
  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  }; // password toggle

  /* end password logic */

  /* start datePicker logic */

  const CustomPopperContainer = ({ children }) => {
    const portalTarget = parentRef.current;
    return portalTarget ? ReactDOM.createPortal(children, portalTarget) : null;
  }; // create an portal for date picker take out side of the clip path

  const months = Array.from({ length: 12 }, (_, i) =>
    new Date(2000, i).toLocaleString("default", { month: "long" })
  ); // get all the months

  const [decadeStart, setDecadeStart] = useState(
    Math.floor(startDate.getFullYear() / 10) * 10
  ); // initial decade as per date

  /* end datePicker logic */

  const parentClasses = `borderWrapper ${className} ${
    Icon &&
    ((iconSide?.toLowerCase() === "left" && "iconLeft") ||
      (iconSide?.toLowerCase() === "right" && "iconRight"))
  } ${size} ${disabled ? "disabled" : ""}`; // class for input parent

  const classes = `input borderInner ${size} ${
    widthSize ? "Width-" + widthSize : ""
  } ${disabled ? "disabled" : ""}`; // class for input

  return (
    <div ref={parentRef}>
      <div
        className={`textbox-wrapper ${
          isPasswordType ? "passwordIsset" : ""
        } ${parentClasses}`}
      >
        {type === "date" ? (
          <>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              popperContainer={CustomPopperContainer}
              popperClassName="datepicker-popper"
              popperPlacement="bottom-start"
              className={classes}
              dateFormat="dd/MM/yyyy"
              renderCustomHeader={({
                date,
                changeYear,
                changeMonth,
                decreaseMonth,
                increaseMonth,
                prevMonthButtonDisabled,
                nextMonthButtonDisabled,
              }) => {
                // Show DEFAULT HEADER if year picker is closed
                return (
                  <div className="custom-header-parent">
                    <div className="custom-header">
                      <button
                        onClick={decreaseMonth}
                        disabled={prevMonthButtonDisabled}
                      >
                        <ArrowLeft size="lg" />
                      </button>
                      <div className="month-year-selectors">
                        <span
                          onClick={() => {
                            setMonthPickerOpen(!isMonthPickerOpen);
                            setYearPickerOpen(false);
                          }}
                          className="clickable"
                        >
                          {months[date.getMonth()]}
                        </span>
                        <span
                          onClick={() => {
                            setYearPickerOpen(true);
                            setMonthPickerOpen(false);
                            setDecadeStart(
                              Math.floor(date.getFullYear() / 10) * 10
                            );
                          }}
                          className="clickable"
                        >
                          {date.getFullYear()}
                        </span>
                      </div>
                      <button
                        onClick={increaseMonth}
                        disabled={nextMonthButtonDisabled}
                      >
                        <ArrowRight size="lg" />
                      </button>
                    </div>
                    {isYearPickerOpen && (
                      <div className="year-header-decade">
                        <div className="decade-header">
                          <button
                            onClick={() => setDecadeStart(decadeStart - 10)}
                          >
                            <ArrowLeft size="lg" />
                          </button>
                          <span>
                            {decadeStart} - {decadeStart + 9}
                          </span>
                          <button
                            onClick={() => setDecadeStart(decadeStart + 10)}
                          >
                            <ArrowRight size="lg" />
                          </button>
                        </div>

                        <div className="year-grid">
                          {Array.from({ length: 10 }, (_, i) => {
                            const year = decadeStart + i;
                            const currentYear = new Date().getFullYear();

                            const isSelected = year === startDate.getFullYear();
                            const isCurrent = year === currentYear;
                            const isFuture = year > currentYear;
                            const isPast = year < currentYear;

                            return (
                              <div
                                key={year}
                                className={`year-option 
                  ${isSelected ? "selected-year" : ""} 
                  ${isCurrent ? "current-year" : ""} 
                  ${isFuture ? "future-year" : ""} 
                  ${isPast ? "past-year" : ""}`}
                                onClick={() => {
                                  changeYear(year);
                                  setStartDate(
                                    new Date(
                                      year,
                                      date.getMonth(),
                                      date.getDate()
                                    )
                                  );
                                  setYearPickerOpen(false);
                                }}
                              >
                                {year}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {isMonthPickerOpen && (
                      <div className="month-picker-dropdown">
                        <div className="month-header">
                          <button
                            onClick={() => {
                              const newYear = date.getFullYear() - 1;
                              const updatedDate = new Date(
                                newYear,
                                date.getMonth(),
                                date.getDate()
                              );
                              changeYear(newYear);
                              setStartDate(updatedDate);
                            }}
                          >
                            <ArrowLeft size="lg" />
                          </button>
                          <span>{date.getFullYear()}</span>
                          <button
                            onClick={() => {
                              const newYear = date.getFullYear() + 1;
                              const updatedDate = new Date(
                                newYear,
                                date.getMonth(),
                                date.getDate()
                              );
                              changeYear(newYear);
                              setStartDate(updatedDate);
                            }}
                          >
                            <ArrowRight size="lg" />
                          </button>
                        </div>

                        <div className="month-grid">
                          {months.map((month, idx) => {
                            const today = new Date();
                            const selectedMonth = date.getMonth();
                            const selectedYear = date.getFullYear();

                            const isSelected =
                              selectedMonth === idx &&
                              selectedYear === startDate.getFullYear();
                            const isCurrent =
                              idx === today.getMonth() &&
                              selectedYear === today.getFullYear();
                            const isFuture =
                              selectedYear > today.getFullYear() ||
                              (selectedYear === today.getFullYear() &&
                                idx > today.getMonth());
                            const isPast =
                              selectedYear < today.getFullYear() ||
                              (selectedYear === today.getFullYear() &&
                                idx < today.getMonth());

                            return (
                              <div
                                key={month}
                                className={`month-option
                        ${isSelected ? "selected-month" : ""}
                        ${isCurrent ? "current-month" : ""}
                        ${isFuture ? "future-month" : ""}
                        ${isPast ? "past-month" : ""}
                      `}
                                onClick={() => {
                                  changeMonth(idx);
                                  setStartDate(
                                    new Date(
                                      date.getFullYear(),
                                      idx,
                                      date.getDate()
                                    )
                                  );
                                  setMonthPickerOpen(false);
                                }}
                              >
                                {month}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            />
            {Icon && <Icon size={size} className="input-icon" />}
          </>
        ) : (
          <>
            {isPasswordType && (
              <Eye
                className={`icon ${showPassword ? "eyeOpen" : ""}`}
                onClick={toggleShowPassword}
              />
            )}
            <input
              type={isPasswordType && !showPassword ? "password" : "text"}
              className={`${classes} ${isPasswordType ? "with-icon" : ""}`}
              name={name}
              placeholder={placeholder}
              value={value}
              disabled={disabled}
              {...props}
            />
            {Icon && <Icon size={size} className="input-icon" />}
          </>
        )}
      </div>
    </div>
  );
};

export default Textbox;
